// @ts-nocheck
import waterSourceApi from '@/api/config/waterSourceApi'
import InputLabelField from '@/components/form-control/InputLabelField'
import Select from '@/components/form-control/Select'
import { useAuthContext } from '@/features/auth/context'
import useUser from '@/features/auth/hooks/useUser'
import CustomOptionFlowValve from '@/features/config/components/sidebar/WaterSourceView/CustomOptionFlowValve'
import CustomSingleFlowValue from '@/features/config/components/sidebar/WaterSourceView/CustomSingleFlowValue'
import { useConfigUI } from '@/features/config/context'
import useAddWaterSource from '@/features/config/hooks/watersource/useAddWaterSource'
import useEditWaterSource from '@/features/config/hooks/watersource/useEditWaterSource'
import useSearchWaterSourceId from '@/features/config/hooks/watersource/useSearchWaterSourceId'
import { NotifyService } from '@/helpers'
import useTranslation from '@/hooks/useTranslation'
import { useWaterCost } from '@/hooks/useWaterCost'
import { DATA_PAGINATION } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { find } from 'lodash'
import { useEffect, useState } from 'react'
import { Controller, Resolver, useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { customOptionFlowSensor, customSingleFlowSensor } from './CustomOption'
import classes from './style.module.scss'

interface FormPipeProps {
  id?: number
  parentId?: number
}

export interface IFormWaterSource {
  id?: number
  name: string
  interfaceId: number
  flowValveId: number
  flowSensorId: number
  description: string
  cost: number
}

interface FlowValveOption {
  id: number
  io: string
  address: number
  stateName: string
}

interface ISensorData {
  id: number
  interfaceId: number
  active: boolean
  flowRuleId: number
  volumeLastTs: number
  volume: number[]
  index: number
  address: number
  measuredFlow: number
  io: string
}

const initialValues = {
  id: 0,
  name: '',
  interfaceId: 0,
  flowValveId: 0,
  flowSensorId: 0,
  description: '',
  cost: undefined,
}

const FormWaterSourceView = ({ id }: FormPipeProps) => {
  const { closeSidebar } = useConfigUI()
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()
  const { data: user } = useUser()

  const {
    convertFromMetric: convertWaterCostFromMetric,
    convertToMetric: convertWaterCostToMetric,
  } = useWaterCost()

  const schema = yup.object({
    // id: yup.number().optional(),
    // interfaceId: yup.number().required('Interface ID is required'),
    name: yup.string().required('Water Source Name is required'),
    flowValveId: yup.number().required('Flow Valve ID is required'),
    flowSensorId: yup.number().required('Flow Sensor ID is required'),
    description: yup.string().nullable().notRequired(),
    cost: yup
      .number()
      .required('Cost is required')
      .positive('Cost must be a positive number')
      .min(0, 'Cost cannot be negative'),
  })

  // get waterSourceById
  const { data, isFetching } = useSearchWaterSourceId(interfaceId, Number(id))

  const { mutate: addWaterSource, isLoading: loadingAddWaterSource } =
    useAddWaterSource()
  const { mutate: updateWaterSource, isLoading: loadingUpdateWaterSource } =
    useEditWaterSource()

  const form = useForm<IFormWaterSource>({
    resolver: yupResolver(schema) as unknown as Resolver<IFormWaterSource, any>,
  })

  const { reset, control, handleSubmit } = form

  const isMetric = !user?.measure?.id || user?.measure?.id === 1
  const unitLabel = isMetric ? 'm³' : 'kGal'
  const costValue = useWatch({ name: 'cost', control })
  const [localCost, setLocalCost] = useState<string | number | undefined>(
    undefined,
  )

  const [selectedFlowValveId, setSelectedFlowValveId] = useState<number>(0)
  const [selectedSensorId, setSelectedSensorId] = useState<number>(0)

  const getFlowValveData =
    () => async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
      let data: DATA_PAGINATION | undefined
      data = await waterSourceApi
        .getListFlowValve({
          interfaceId,
          ...(id && { waterSourceId: id }),
        })
        .then((res) => {
          return res
        })
        .catch((e) => {
          NotifyService.error(e)
          return undefined
        })
      return data
    }

  const getFlowSensorData =
    () => async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
      let data: DATA_PAGINATION | undefined
      data = await waterSourceApi
        .getListFlowSensor({
          interfaceId,
          ...(id && { waterSourceId: id }),
        })
        .then((res) => {
          return res
        })
        .catch((e) => {
          NotifyService.error(e)
          return undefined
        })
      return data
    }

  const handleSave = (data: IFormWaterSource) => {
    if (id) {
      updateWaterSource({ ...data, id })
    } else {
      addWaterSource({ ...data, interfaceId })
    }
  }

  useEffect(() => {
    ;(async () => {
      if (id && data) {
        reset({ ...data, id })
        setLocalCost(
          isMetric ? data.cost : convertWaterCostFromMetric(data.cost),
        )
        const flowValveData = await getFlowValveData()()
        const flowSensorData = await getFlowSensorData()()

        if (data?.flowValveId)
          setSelectedFlowValveId(
            find(flowValveData?.data, (fv: any) => fv.id === data.flowValveId),
          )
        if (data?.flowSensorId)
          setSelectedSensorId(
            find(
              flowSensorData?.data,
              (fs: any) => fs.id === data?.flowSensorId,
            ),
          )
      } else {
        reset({ ...initialValues, interfaceId })
        setLocalCost(undefined)
      }
    })()
  }, [data, id])

  useEffect(() => {
    if (
      localCost === undefined &&
      costValue !== undefined &&
      costValue !== null
    ) {
      const initialDisplay = isMetric
        ? costValue
        : convertWaterCostFromMetric(costValue)
      setLocalCost(initialDisplay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costValue, localCost, isMetric])

  // useEffect(() => {
  //   console.log('form Watch', form.getValues())
  // }, [form.watch()])

  return (
    <Container className={classes.container}>
      {isFetching && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isFetching && (
        <Fade in={!isFetching}>
          <form onSubmit={handleSubmit(handleSave)}>
            <Stack className={classes.formEdit}>
              <Stack className={classes.wrapper}>
                <Typography className={classes.editTitlePipe}>
                  {!id && t('waterSourceAddNew')}
                  {!!id && t('dataConfigEditWaterSource')}
                </Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    size="small"
                    className={classes.btnCancel}
                    onClick={() => closeSidebar()}
                  >
                    {t('commonActionsCancel')}
                  </Button>
                  <LoadingButton
                    type="submit"
                    size="small"
                    variant="primary"
                    className={classes.loadingBtn}
                    loading={
                      id ? loadingUpdateWaterSource : loadingAddWaterSource
                    }
                  >
                    {t('commonActionsSave')}
                  </LoadingButton>
                </Stack>
              </Stack>

              <Stack>
                <InputLabelField
                  isRequired
                  name="name"
                  // inputRef={inputRefSiteName}
                  form={form}
                  label={t('waterSourceName')}
                  placeholder={t('waterSourceName')}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    width: {},
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />

                <Controller
                  name={'flowValveId'}
                  control={control}
                  render={({ field }) => (
                    <Select
                      isSearchable={true}
                      required
                      minWidth={'100%'}
                      isHasMore
                      label={t('flowValve')}
                      name="flowValveId"
                      bindLabel="io"
                      bindKey="id"
                      hasLoadMoreItem={false}
                      placeholder={t(
                        'addWaterSourcePlaceHolderSelectFlowValve',
                      )}
                      getOptions={getFlowValveData()}
                      value={selectedFlowValveId}
                      components={{
                        Option: CustomOptionFlowValve,
                        SingleValue: CustomSingleFlowValue,
                      }}
                      onChange={(val: any) => {
                        if (val?.id) {
                          field.onChange(val.id)
                        } else {
                          field.onChange(null)
                        }
                        setSelectedFlowValveId(val)
                      }}
                    />
                  )}
                />

                <Controller
                  name={'flowSensorId'}
                  control={control}
                  render={({ field }) => (
                    <Select
                      required
                      minWidth={'100%'}
                      isHasMore
                      isSearchable={true}
                      label={t('flowSensor')}
                      name="flowSensorId"
                      bindLabel="io"
                      bindKey="id"
                      placeholder={t(
                        'addWaterSourcePlaceHolderSelectFlowSensor',
                      )}
                      hasLoadMoreItem={false}
                      getOptions={getFlowSensorData()}
                      value={selectedSensorId}
                      components={{
                        Option: customOptionFlowSensor,
                        SingleValue: customSingleFlowSensor,
                      }}
                      onChange={(val: any) => {
                        if (val?.id) {
                          field.onChange(val.id)
                        } else {
                          field.onChange(null)
                        }
                        setSelectedSensorId(val)
                      }}
                    />
                  )}
                />

                <Controller
                  name="cost"
                  control={control}
                  defaultValue={0}
                  render={({
                    field: { value, onChange, ...field },
                    fieldState: { error },
                  }) => {
                    return (
                      <InputLabelField
                        {...field}
                        isRequired
                        error={!!error}
                        helperText={error?.message}
                        label={`${t('waterCost')} ($/${unitLabel})`}
                        placeholder="0.00"
                        value={localCost !== undefined ? localCost : value}
                        onChange={(e) => {
                          const inputValue = e.target.value
                          setLocalCost(inputValue)
                          if (inputValue === '' || isNaN(Number(inputValue))) {
                            onChange(undefined)
                            return
                          }
                          const metricValue =
                            convertWaterCostToMetric(inputValue)
                          onChange(metricValue)
                        }}
                        style={{
                          height: 50,
                          borderRadius: '4px',
                          marginTop: '12px',
                          width: {},
                        }}
                        padding="8px 16px"
                      />
                    )
                  }}
                />
              </Stack>
            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default FormWaterSourceView
